<template lang="pug">
v-container.px-sm-8.py-sm-4
  .text-center.mt-6(v-if="isLoading")
    v-progress-circular(width="2" size="50" color="primary" indeterminate)
  template(v-else)
    template(v-if="task")
      v-row.mt-sm-8(no-gutters)
        v-col(cols="12" md="6")
          .d-flex.align-center
            template(v-if="!isEditing")
              .text-2xl.font-semibold.mr-2 {{ task.title }}
              v-btn(@click="editTaskName" color="primary400" icon)
                v-icon(small) $yzi-edit-02
            template(v-else)
              v-form(ref="form")
                yc-text-field.mr-2(label="Task name" v-model="editedName" :rules="rules.not_empty" validate-on-blur)
              v-btn.button-sm(@click="saveEdit" :loading="isSaving" color="info" height=40 depressed) Save 
              v-btn.button-sm(@click="isEditing = false" color="primary400" height=40 text) Cancel
          .mt-1.text-sm.font-normal.primary400--text 
            span {{ $t('created') }} {{ task.created | displayDate }}
            span.ml-4(v-if="task.due_date") {{ $t('due_by') }} {{ task.due_date | displayDate }}
          .text-sm.primary400--text.mt-1(v-if="task.group")
            | Segments: 
            template(v-for="group in task.group")
              y-chip.ma-1(:label="group.title" outlined)
        
        v-col(cols="12" md="6")
          .text-right
            v-btn.text-none.mr-2.pl-2(@click="openTasks" height="40" text)
              eva-icon.mt-1(name="chevron-left") 
              .link-s {{ $t('back_to_tasks') }}
            v-menu(offset-y)
              template(v-slot:activator="{ on, attrs }")
                v-btn(v-on="on" color="black" icon)
                  v-icon mdi-dots-horizontal
              v-list
                v-list-item(@click="showTemplateDialog = true")
                  .text-sm {{ $t('message_template') }}
                v-list-item(@click="showDeleteDialog = true")
                  .text-sm {{ $t('delete_task') }}
      v-divider.mt-4.mb-8
      v-row
        v-col(cols="6")
          v-card.pa-4.round-lg(outlined)
            .text-md.font-semibold.neutral500--text {{ $t('customers_engaged') }}
            .text-2xl.font-semibold.mt-2 {{ task.total_engaged_customer }}
            .d-flex.mt-4.align-center
              eva-icon(name="chevron-up" :fill="$colors.success500" v-if="getEngagedPercentage() > 0")
              eva-icon(name="chevron-down" :fill="$colors.danger500" v-if="getEngagedPercentage() < 0")
              .text-xs.neutral500--text.mb-2 {{ getEngagementRatePercentage() }}% {{ $t('from_yesterday') }}
        v-col(cols="6")
          v-card.pa-4.round-lg(outlined)
            .text-md.font-semibold.neutral500--text {{ $t('engagement_rate') }} (%)
            .text-2xl.font-semibold.mt-2 {{ getTotalEngagementRate() }}%
            .d-flex.mt-4.align-center
              eva-icon(name="chevron-up" :fill="$colors.success500"  v-if="getEngagementRatePercentage() > 0")
              eva-icon(name="chevron-down" :fill="$colors.danger500" v-if="getEngagementRatePercentage() < 0")
              .text-xs.neutral500--text.mb-2 {{ getEngagementRatePercentage() }}% {{ $t('from_yesterday') }}
      
      .mt-sm-10
      v-btn.px-2.button-AA-small(@click="addAssignee" height="48" color="info500" text)
        eva-icon(name="plus-circle-outline" :fill="$colors.info500") 
        .ml-2 {{ $t('add_assignees') }}
      v-data-table.mt-3(:headers="headers" :items="assignees" :items-per-page="assignees.length" :hide-default-header="isMobileScreen" item-key="id" hide-default-footer disable-sort)
        template(v-slot:no-data)
          .mt-16
            v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
            .mt-2 {{ $t('no_list_available') }}
        template(v-slot:item.action="{ item }")
          .text-right
            v-btn.my-4.button-sm(@click="removeAgent(item)" height=40 outlined) {{ $t('remove') }}
        template(v-slot:item.user="{ item }")
          .font-medium {{ `${item.user.first_name} ${item.user.last_name}` }}
        template(v-if="isMobileScreen" v-slot:item="{ item }")
          .py-3
            .mb-2.d-flex.align-center
              .text-md.font-medium.flex-grow-1 {{ `${item.user.first_name} ${item.user.last_name}` }}
              v-menu(offset-y min-width=72)
                template(v-slot:activator="{ on }")
                  v-btn(v-on="on" icon small)
                    v-icon mdi-dots-horizontal
                v-list(dense)
                  v-list-item(@click="removeAgent(item)")
                    .text-sm.danger--text {{ $t('remove') }}
            
            .d-flex.mb-1.text-sm
              .flex-grow-1
                .basic600--text {{ $t('phone_number') }}
              div {{ item.phone }}
            .d-flex.mb-1.text-sm
              .flex-grow-1
                .basic600--text {{ $t('engaged_customers') }}
              div {{ item.total_engaged_customer }}
          v-divider
    template(v-else)
      .primary400--text {{ $t('task_not_found') }}
    delete-task-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :task="task")
    delete-agent-dialog(v-if="showDeleteAgentDialog" :open.sync="showDeleteAgentDialog" :deleteId="deleteId" :task="task" @deleted="reload")
    add-agent-dialog(v-if="showAddDialog" :open.sync="showAddDialog" :task="task" @edited="fetchTask")
    message-template-dialog(v-if="showTemplateDialog" :open.sync="showTemplateDialog" :task="task" @updated="fetchTask")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import DeleteTaskDialog  from '@/components/connect/dialogs/DeleteTaskDialog.vue'
import DeleteAgentDialog  from '@/components/connect/dialogs/DeleteAgentDialog.vue'
import AddAgentDialog from '@/components/connect/dialogs/AddAgentDialog.vue'
import MessageTemplateDialog from '@/components/connect/dialogs/MessageTemplateDialog.vue'

export default {
  props: ['id'],
  components: { 
    DeleteTaskDialog, 
    DeleteAgentDialog, 
    AddAgentDialog,
    MessageTemplateDialog
  },
  data: () => ({
    task: null,
    isEditing: false,
    isLoading: true,
    isSaving: false,
    editedName: '',
    showDeleteDialog: false,
    showAddDialog: false,
    showDeleteAgentDialog: false,
    showTemplateDialog: false,
    rules: {
      not_empty: [v => v.trim().length || 'This field is required']
    },
    assignees: [],
    headers: [
      {
        text: 'Assignees',
        value: 'user'
      },
      {
        text: 'Phone',
        value: 'phone',
      },
      {
        text: 'Engaged Customers',
        value: 'total_engaged_customer',
      },
      {
        text: '',
        value: 'action'
      },
    ],
    deleteId: null
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    }
  },
  created() {
    this.headers.map(item => item['class'] = 'text-table-header')
    this.fetchTask()
  },  
  methods: {
    ...mapActions(['showMessage']),
    fetchTask() {
      this.isLoading = true
      this.$axios.get(`/connect/${this.company.slug}/tasks/${this.id}?expand=assign_to.user,template,group`)
      .then(resp => {
        this.task = resp.data
        this.assignees = resp.data.assign_to
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      })
      .finally(() => {
        this.isLoading = false
      })
    },
    editTaskName() {
      this.isEditing = true
      this.editedName = this.task.title
    },
    saveEdit() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isSaving = true
      this.$axios.patch(`/connect/${this.company.slug}/tasks/${this.id}`, { title: this.editedName.trim() })
      .then(resp => {
        this.isEditing = false
        this.showMessage(['success', this.$t('success_message.task_name_changed_successfully')])
        this.task = resp.data
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_change_task_name')])
      })
      .finally(() => {
        this.isSaving = false
      })
    },
    openTasks() {
      this.$router.push('/admin/connect/tasks')
    },
    removeAgent(agent) {
      this.deleteId = agent.id
      this.showDeleteAgentDialog = true
    },
    addAssignee() {
      this.showAddDialog = true
    },
    getEngagedPercentage() {
      return this.getPercentage(this.task.total_engaged_customer, this.task.total_engaged_customer_yesterday)
    },
    getTotalEngagementRate() {
      const percentage = (this.task.total_engaged_customer / this.task.total_customer) * 100 || 0
      return percentage.toFixed(2)
    },
    getEngagementRatePercentage() {
      const percentage = ((this.task.total_engaged_customer - this.task.total_engaged_customer_yesterday) / this.task.total_customer) * 100 || 0
      return percentage.toFixed(2)
    },
    getPercentage(newNum, oldNum) {
      const percentage = ((newNum - oldNum) / oldNum) * 100 || 0
      return percentage.toFixed(2)
    },
    reload() {
      this.fetchTask()
    }
  }
}
</script>